import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from './cn'
import { Icon, type IconName } from './Icon'

/**
 * The button — a port of `.btn` and its variants in `base.css`, plus `Icons/IconButton.razor`.
 *
 * One component rather than two. `IconButton` in the original is `.btn` with an icon slot and a
 * different default variant; splitting that into a separate React component would mean two files
 * whose styling has to be kept in step by hand, which is how the Blazor app ended up with a
 * `.btn-group-sm .btn-sm` override that only applies in one context.
 *
 * Variants map to the same tokens the CSS uses, so a theme change reaches them:
 * primary/secondary/danger from the `btn-*` token families, ghost from `text-secondary` + `hover-bg`,
 * outline from the secondary border with no fill.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline'
export type ButtonSize = 'sm' | 'md' | 'lg'

const VARIANT: Record<ButtonVariant, string> = {
  primary:
    'bg-btn-primary-bg text-btn-primary-fg border-btn-primary-bg hover:bg-btn-primary-hover hover:border-btn-primary-hover',
  secondary:
    'bg-btn-secondary-bg text-btn-secondary-fg border-btn-secondary-border hover:bg-btn-secondary-hover',
  danger:
    'bg-btn-danger-bg text-btn-danger-fg border-btn-danger-bg hover:bg-btn-danger-hover hover:border-btn-danger-hover',
  ghost: 'bg-transparent text-fg-secondary border-transparent hover:bg-bg-hover hover:text-fg',
  outline:
    'bg-transparent text-fg-secondary border-btn-secondary-border hover:bg-bg-hover hover:text-fg',
}

/** `.btn` is spacing-sm/md; `.btn-sm` drops to xs/sm at font-size-xs; `.btn-lg` rises to md/lg. */
const SIZE: Record<ButtonSize, string> = {
  sm: 'px-sm py-xs text-xs',
  md: 'px-md py-sm text-sm',
  lg: 'px-lg py-md text-md',
}

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Drawn before the label, at 16px — the size `.btn svg` forces in the original. */
  icon?: IconName
  /** Replaces the icon with a spinner and disables the button. */
  busy?: boolean
  children?: ReactNode
  className?: string
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  busy = false,
  disabled,
  children,
  className,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      // Explicit rather than inherited: a button inside a form defaults to `submit`, and the Blazor
      // components all passed `type="button"` for that reason.
      type={type}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={cn(
        'inline-flex items-center justify-center gap-xs rounded-md border font-medium leading-none transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANT[variant],
        SIZE[size],
        className,
      )}
      {...rest}
    >
      {busy ? (
        <span
          className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-line border-t-accent"
          aria-hidden
        />
      ) : icon ? (
        <Icon name={icon} size="sm" />
      ) : null}
      {children}
    </button>
  )
}

/**
 * An icon-only button — `IconButton.razor` with no `Text`.
 *
 * `label` is required rather than optional, because with no text the icon *is* the label: an
 * unlabelled icon button is a control a screen-reader user cannot identify at all. The original's
 * `Title` was optional and several call sites omitted it.
 */
export interface IconButtonProps extends Omit<ButtonProps, 'children' | 'icon'> {
  icon: IconName
  label: string
}

export function IconButton({
  icon,
  label,
  size = 'sm',
  variant = 'ghost',
  className,
  ...rest
}: IconButtonProps) {
  return (
    <Button
      variant={variant}
      size={size}
      title={label}
      aria-label={label}
      // `.btn-group-sm .btn-sm` gives these a 32px minimum so a row of icon actions stays an even
      // grid rather than jittering with each icon's width.
      //
      // **Merged, not spread.** `className` arrives in `rest`, so writing it as a literal attribute
      // above `{...rest}` let any caller's class *replace* the sizing rather than add to it — and
      // four pages do exactly that (`className="text-danger"` on every destructive row action in
      // AdminDevices, DeviceTypes, Locations and Brands), so those buttons have been rendering
      // without their 32px floor. Destructuring it out is what makes `cn` the only path.
      className={cn(
        'min-h-8 min-w-8 px-xs',
        // Delete is neutral until pointed at, on every page: a red bin on every row of an admin table read
        // as an alarm (Plans/experience-review-2026-10-10.md). A page asking for `text-danger` gets this.
        icon === 'delete' ? 'text-fg-secondary hover:bg-danger-bg hover:text-danger' : undefined,
        icon === 'delete' ? className?.replace(/(^|\s)text-danger(?=\s|$)/g, '') : className,
      )}
      {...rest}
    >
      <Icon name={icon} size="sm" />
    </Button>
  )
}

/** The actions cell of a table row — `Tables/DataTableActions.razor`. */
export function ActionGroup({ children }: { children: ReactNode }) {
  return <div className="flex gap-xs">{children}</div>
}
